import {
  ThunkExternalLogin,
  ThunkGetPusherAuthSettings,
  ThunkLinkExternalAccount,
  ThunkUnlinkExternalAccount,
} from 'store/auth/thunks';
import Pusher from 'pusher-js';

import { appConfig } from 'configs/appConfig';

import { useAppDispatch } from 'store/hooks';
import { IPusherConfig } from 'services/chats/types';
import { AsyncThunk } from '@reduxjs/toolkit';
import { changeLinkState } from 'store/user';
import { enqueueSnackbar } from 'notistack';
import { ILoginResponse } from 'services/auth/types';
import { LOCAL_STORAGE_REFRESH_TOKEN, LOCAL_STORAGE_TOKEN } from 'constants/storage';
import { HOME } from 'router/routes';
import { getPostLoginRedirectPath } from 'utils/postLoginRedirect';
import { useLocation, useNavigate } from 'react-router-dom';

interface IHook {
  handleLinking: (type: 'Microsoft' | 'Google') => void;
  handleExternalLogin: (type: 'Microsoft' | 'Google') => void;
  handeUnlinking: (type: 1 | 2) => void;
}

export const useAuthApp = (setProcessing: (arg: boolean) => void): IHook => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAfterLogin = getPostLoginRedirectPath(
    (location.state as { from?: { pathname: string; search: string } } | null)?.from,
    HOME,
  );

  const createPusherChannel = async (
    pusherConfig: IPusherConfig,
    guid: string,
    thunk: AsyncThunk<any, any, any>,
    successCallback: (res: boolean | ILoginResponse) => void,
  ): Promise<void> => {
    const pusher = new Pusher(pusherConfig.key, {
      cluster: pusherConfig.cluster,
      channelAuthorization: {
        transport: 'ajax',
        endpoint: `${appConfig.baseUrl}/ClientPortalAuth/authorize-auth-pusher`,
      },
    });

    const [channel] = await Promise.all([pusher.subscribe(`private-${guid}`)]);

    channel.bind('client-message', (data: any) => {
      dispatch(thunk({ tokenType: data.type, token: data.token }))
        .unwrap()
        .then(res => {
          successCallback(res);
          setProcessing(false);
          channel.unsubscribe();
          pusher.disconnect();
        });

      // Kill Pusher and Channel after 5 min to prevent stacking pusher connection
      setTimeout(() => {
        if (channel && pusher) {
          channel.unbind_all();
          channel.unsubscribe();
          pusher.disconnect();
        }
      }, 300000);
    });
  };

  const handleLinking = (type: 'Microsoft' | 'Google'): void => {
    setProcessing(true);
    dispatch(ThunkGetPusherAuthSettings())
      .unwrap()
      .then(async res => {
        const guid = crypto.randomUUID();

        const successCallback = (): void => {
          dispatch(changeLinkState({ type, state: true }));
          enqueueSnackbar('Successfully linked', { variant: 'success' });
        };

        await createPusherChannel(res, guid, ThunkLinkExternalAccount, successCallback);

        window.open(`${appConfig.authUrl}?type=${type}&sessionId=${guid}`, '_blank');
      })
      .catch(() => {
        setProcessing(false);
      });
  };

  const handleExternalLogin = (type: 'Microsoft' | 'Google'): void => {
    setProcessing(true);
    dispatch(ThunkGetPusherAuthSettings())
      .unwrap()
      .then(async res => {
        const guid = crypto.randomUUID();

        const successCallback = (res: ILoginResponse | boolean): void => {
          if (typeof res !== 'boolean') {
            localStorage.setItem(LOCAL_STORAGE_TOKEN, res.access_token);
            localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, res.refresh_token);
            navigate(redirectAfterLogin, { replace: true });
          }
        };

        await createPusherChannel(res, guid, ThunkExternalLogin, successCallback);

        window.open(`${appConfig.authUrl}?type=${type}&sessionId=${guid}`, '_blank');
      })
      .catch(() => {
        setProcessing(false);
      });
  };

  const handeUnlinking = (type: 1 | 2): void => {
    setProcessing(true);
    dispatch(ThunkUnlinkExternalAccount(type))
      .unwrap()
      .then(() => {
        dispatch(changeLinkState({ type: type === 1 ? 'Microsoft' : 'Google', state: false }));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  return { handleLinking, handeUnlinking, handleExternalLogin };
};
